"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { RegressionStrings } from "@/components/tools/multiple-regression-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import type { Design } from "@/lib/tool-kit/stats-regression/design";
import { type OlsReport, predictAt } from "@/lib/tool-kit/stats-regression/ols";
import {
  apaBounded,
  apaPClause,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

interface Props {
  report: OlsReport;
  design: Design;
  termNames: string[];
  responseName: string;
  strings: RegressionStrings;
  fmt: NumberFormatter;
}

const VIF_SERIOUS = 10;
const VIF_MODERATE = 5;
const SHOWN_CASES = 10;

function interval(
  low: number,
  high: number,
  fmt: NumberFormatter,
  strings: RegressionStrings
): string {
  return t(strings.ciTemplate, {
    low: formatStat(fmt, low),
    high: formatStat(fmt, high),
  });
}

function equation(
  report: OlsReport,
  termNames: string[],
  fmt: NumberFormatter,
  strings: RegressionStrings
): string {
  let text = t(strings.equationIntercept, {
    a: formatStat(fmt, report.coefficients[0].estimate),
  });
  for (const [j, name] of termNames.entries()) {
    const row = report.coefficients[j + 1];
    if (!row.aliased) {
      text += t(
        row.estimate < 0 ? strings.equationMinus : strings.equationPlus,
        {
          b: formatStat(fmt, Math.abs(row.estimate)),
          name,
        }
      );
    }
  }
  return text;
}

function Headline({
  report,
  design,
  termNames,
  responseName,
  strings,
  fmt,
}: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const k = report.dfModel;
  const apa = t(strings.apaSummary, {
    r2: apaBounded(fmt, report.r2, 2),
    adj: apaBounded(fmt, report.adjR2, 2),
    df1: fmt(k),
    df2: fmt(report.dfResidual),
    f: fixed(fmt, report.f, 2),
    p: apaPClause(fmt, report.pF),
  });
  return (
    <div aria-live="polite" className="space-y-4">
      <div className={`${RESULT_CARD} text-center`}>
        <p className="text-muted-foreground text-sm">{strings.equationTitle}</p>
        <p className="break-words font-mono font-semibold text-foreground text-lg">
          {equation(report, termNames, fmt, strings)}
        </p>
      </div>
      <StatGrid>
        <StatCard emphasis label={strings.rSquared} value={num(report.r2)} />
        <StatCard label={strings.adjRSquared} value={num(report.adjR2)} />
        <StatCard
          label={t(strings.fTest, { df1: fmt(k), df2: fmt(report.dfResidual) })}
          value={num(report.f)}
        />
        <StatCard
          label={strings.fPValue}
          value={formatPValue(fmt, report.pF)}
        />
        <StatCard label={strings.residualSe} value={num(report.sigma)} />
        <StatCard label={strings.casesN} value={fmt(design.y.length)} />
      </StatGrid>
      <Explanation>
        {t(strings.summary, {
          pct: fmt(report.r2, { style: "percent", maximumFractionDigits: 1 }),
          y: responseName,
          adj: num(report.adjR2),
          verdict:
            report.pF < 0.05
              ? strings.verdictSignificantly
              : strings.verdictNotSignificantly,
          p: formatPRelation(fmt, report.pF),
        })}
      </Explanation>
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-mono text-foreground text-sm">{apa}</p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          small
          text={apa}
        />
      </div>
    </div>
  );
}

function CoefficientTables({ report, termNames, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const levelText = fmt(report.level, { style: "percent" });
  const rows = report.coefficients.map((row, j) => ({
    row,
    name: j === 0 ? strings.termIntercept : termNames[j - 1],
  }));
  const aliased = rows.filter((entry) => entry.row.aliased);
  const vifs = rows.filter((entry) => entry.row.vif !== null);
  const serious = vifs.filter(
    (e) => (e.row.vif ?? 0) > VIF_SERIOUS && !e.row.aliased
  );
  const moderate = vifs.filter(
    (e) =>
      (e.row.vif ?? 0) > VIF_MODERATE &&
      (e.row.vif ?? 0) <= VIF_SERIOUS &&
      !e.row.aliased
  );
  const join = (list: typeof rows) =>
    list.map((e) => e.name).join(strings.namesSeparator);
  const cell = (
    value: number | null,
    aliasedRow: boolean,
    digits?: boolean
  ) => {
    if (aliasedRow) {
      return strings.aliasedTerm;
    }
    if (value === null) {
      return "";
    }
    return digits ? formatStat(fmt, value, 2) : num(value);
  };
  const apaHead = [
    strings.colTerm,
    strings.colB,
    strings.colSe,
    strings.colBeta,
    strings.colT,
    strings.colP,
  ];
  const apaRows = rows.map(({ row, name }) => [
    name,
    cell(row.estimate, row.aliased),
    cell(row.se, row.aliased),
    row.beta === null ? "" : apaBounded(fmt, row.beta, 2),
    cell(row.t, row.aliased, true),
    row.aliased ? strings.aliasedTerm : formatP(fmt, row.p, true),
  ]);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.coefficientsTitle}</h3>
      <ResultTable
        head={[
          strings.colTerm,
          strings.colB,
          strings.colSe,
          strings.colBeta,
          strings.colT,
          strings.colP,
          t(strings.colCi, { level: levelText }),
          strings.colVif,
        ]}
        rows={rows.map(({ row, name }) => [
          name,
          cell(row.estimate, row.aliased),
          cell(row.se, row.aliased),
          cell(row.beta, row.aliased),
          cell(row.t, row.aliased, true),
          row.aliased ? strings.aliasedTerm : formatPValue(fmt, row.p),
          row.aliased
            ? strings.aliasedTerm
            : interval(row.ciLow, row.ciHigh, fmt, strings),
          row.vif === null ? "" : cell(row.vif, false, true),
        ])}
        wrapFirstColumn
      />
      <Explanation>{strings.betaNote}</Explanation>
      {aliased.length > 0 ? (
        <Notice>{t(strings.aliasedNote, { names: join(aliased) })}</Notice>
      ) : null}
      {serious.length > 0 ? (
        <Notice>{t(strings.vifSerious, { names: join(serious) })}</Notice>
      ) : null}
      {moderate.length > 0 ? (
        <Notice>{t(strings.vifModerate, { names: join(moderate) })}</Notice>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <span className="text-muted-foreground text-sm">
          {strings.apaTitle}
        </span>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.apaCopy}
          small
          text={[apaHead, ...apaRows].map((line) => line.join("\t")).join("\n")}
        />
      </div>
    </div>
  );
}

function FitTables({ report, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.anovaTitle}</h3>
        <ResultTable
          footer={[
            strings.sourceTotal,
            fmt(report.n - 1),
            num(report.sst),
            "",
            "",
          ]}
          head={[
            strings.colSource,
            strings.colDf,
            strings.colSs,
            strings.colMs,
            strings.colF,
          ]}
          rows={[
            [
              strings.sourceRegression,
              fmt(report.dfModel),
              num(report.ssr),
              num(report.ssr / report.dfModel),
              num(report.f),
            ],
            [
              strings.sourceResidual,
              fmt(report.dfResidual),
              num(report.sse),
              num(report.sse / report.dfResidual),
              "",
            ],
          ]}
          wrapFirstColumn
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.fitCriteriaTitle}</h3>
        <ResultTable
          head={[strings.colCriterion, strings.colValue]}
          rows={[
            [strings.criterionAic, num(report.aic)],
            [strings.criterionBic, num(report.bic)],
          ]}
          wrapFirstColumn
        />
        <Explanation>{strings.criterionNote}</Explanation>
      </div>
    </div>
  );
}

function Plots({ report, design, strings, fmt }: Props) {
  const fitted = report.cases.map((c) => c.fitted);
  const residuals = report.cases.map((c) => c.residual);
  const ends = [Math.min(...fitted), Math.max(...fitted)];
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.observedPlotTitle}</h3>
        <XYPlot
          diagonal
          fmt={fmt}
          label={t(strings.observedPlotLabel, { n: fmt(design.y.length) })}
          series={[
            {
              kind: "points",
              xs: fitted,
              ys: design.y,
              color: 0,
              label: strings.seriesCases,
            },
          ]}
          xLabel={strings.axisPredicted}
          yLabel={strings.axisObserved}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.residualPlotTitle}</h3>
        <XYPlot
          fmt={fmt}
          label={t(strings.residualPlotLabel, { n: fmt(design.y.length) })}
          series={[
            {
              kind: "points",
              xs: fitted,
              ys: residuals,
              color: 0,
              label: strings.seriesCases,
            },
            {
              kind: "line",
              xs: ends,
              ys: [0, 0],
              color: 1,
              label: strings.seriesZero,
              dashed: true,
            },
          ]}
          xLabel={strings.axisPredicted}
          yLabel={strings.axisResidual}
        />
      </div>
      <p className="text-muted-foreground text-sm lg:col-span-2">
        {strings.plotNote}
      </p>
    </div>
  );
}

function flagsFor(
  report: OlsReport,
  index: number,
  strings: RegressionStrings
): string[] {
  const c = report.cases[index];
  const flags: string[] = [];
  if (Math.abs(c.standardized) > 2) {
    flags.push(strings.flagResidual);
  }
  if (c.cooks > 4 / report.n) {
    flags.push(strings.flagInfluential);
  }
  if (c.leverage > (2 * report.rank) / report.n) {
    flags.push(strings.flagLeverage);
  }
  return flags;
}

function Cases({ report, design, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const flagged = report.cases
    .map((c, index) => ({ c, index, flags: flagsFor(report, index, strings) }))
    .filter((entry) => entry.flags.length > 0)
    .sort((a, b) => b.c.cooks - a.c.cooks)
    .slice(0, SHOWN_CASES);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.casesTitle}</h3>
      {flagged.length === 0 ? (
        <Explanation>{strings.casesNone}</Explanation>
      ) : (
        <ResultTable
          head={[
            strings.colCase,
            strings.colObserved,
            strings.colFitted,
            strings.colResidual,
            strings.colStdResidual,
            strings.colLeverage,
            strings.colCooks,
            strings.colFlags,
          ]}
          rows={flagged.map(({ c, index, flags }) => [
            fmt(design.rows[index] + 1),
            num(design.y[index]),
            num(c.fitted),
            num(c.residual),
            num(c.standardized),
            num(c.leverage),
            num(c.cooks),
            flags.join(strings.flagSeparator),
          ])}
        />
      )}
      <Explanation>{strings.casesNote}</Explanation>
    </div>
  );
}

function Predict({ report, design, termNames, strings, fmt }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const parsed = termNames.map((name) => parseNumberInput(values[name] ?? ""));
  const ready = parsed.every((value) => value !== null);
  const point = ready ? predictAt(report, parsed as number[]) : null;
  const num = (value: number) => formatStat(fmt, value);
  const levelText = fmt(report.level, { style: "percent" });
  const maxLeverage = Math.max(...report.cases.map((c) => c.leverage));
  const fillMeans = () =>
    setValues(
      Object.fromEntries(
        termNames.map((name, j) => {
          const column = design.predictors[j];
          const mean = column.reduce((a, b) => a + b, 0) / column.length;
          return [
            name,
            fmt(mean, { maximumFractionDigits: 4, useGrouping: false }),
          ];
        })
      )
    );
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.predictTitle}</h3>
      <div className="grid gap-4 sm:grid-cols-3">
        {termNames.map((name, j) => (
          <TextField
            id={`mr-new-${j}`}
            key={name}
            label={name}
            numeric
            onChange={(value) => setValues((v) => ({ ...v, [name]: value }))}
            value={values[name] ?? ""}
          />
        ))}
      </div>
      <button className={BUTTON_SECONDARY} onClick={fillMeans} type="button">
        {strings.tryExample}
      </button>
      {point ? (
        <div aria-live="polite" className="space-y-3">
          <StatGrid>
            <StatCard
              emphasis
              label={strings.predictedY}
              value={num(point.fitted)}
            />
            <StatCard
              label={t(strings.confidenceInterval, { level: levelText })}
              value={interval(
                point.confidence[0],
                point.confidence[1],
                fmt,
                strings
              )}
            />
            <StatCard
              label={t(strings.predictionInterval, { level: levelText })}
              value={interval(
                point.prediction[0],
                point.prediction[1],
                fmt,
                strings
              )}
            />
          </StatGrid>
          {point.leverage > maxLeverage ? (
            <Notice>
              {t(strings.extrapolation, {
                h: num(point.leverage),
                max: num(maxLeverage),
              })}
            </Notice>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Working({ report, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const k = report.dfModel;
  const common = {
    sse: num(report.sse),
    sst: num(report.sst),
    r2: num(report.r2),
    oneMinus: num(1 - report.r2),
    df: fmt(report.dfResidual),
    k: fmt(k),
  };
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Explanation>{t(strings.workFit, { k: fmt(k) })}</Explanation>
      <Formula>{t(strings.workR2, common)}</Formula>
      <Formula>
        {t(strings.workAdj, {
          ...common,
          nm1: fmt(report.n - 1),
          adj: num(report.adjR2),
        })}
      </Formula>
      <Formula>{t(strings.workF, { ...common, f: num(report.f) })}</Formula>
      <Formula>
        {t(strings.workSigma, { ...common, sigma: num(report.sigma) })}
      </Formula>
      <Explanation>{strings.assumptions}</Explanation>
    </WorkingPanel>
  );
}

export function RegressionResults(props: Props) {
  return (
    <>
      <Headline {...props} />
      <CoefficientTables {...props} />
      <FitTables {...props} />
      <Plots {...props} />
      <Cases {...props} />
      <Predict {...props} />
      <Working {...props} />
    </>
  );
}
